import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, LogOut, Plus, ChevronDown, Zap, CreditCard, Bell, Calendar, AlertCircle, X, BarChart2, Brush } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import Logo from "./Logo";
import Footer from "./Footer";
import { LocaleSwitcher } from "./LocaleSwitcher";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_DOT = {
  "TikTok": "#ff4757", "Instagram Reels": "#e1306c",
  "YouTube Shorts": "#ff0000", "Snapchat": "#FFFC00", "Facebook": "#1877f2",
};

function getCountdown(scheduledAt, t) {
  const diff = new Date(scheduledAt) - new Date();
  if (diff < 0) return t("notifications.overdue");
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(h / 24);
  if (d > 0) return t("notifications.inDays", { count: d });
  if (h > 0) return t("notifications.inHours", { count: h });
  const m = Math.floor(diff / 60000);
  return m > 0 ? t("notifications.inMinutes", { count: m }) : t("notifications.now");
}

function NotificationBell({ user }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const ref = useRef(null);
  const notifiedRef = useRef(new Set());
  const permissionRef = useRef(false);

  const fetchPosts = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/schedule`, { withCredentials: true });
      const pending = (res.data || []).filter(p => p.status !== "posted");
      setPosts(pending);
      return pending;
    } catch { return []; }
  }, [user]);

  // Request browser notification permission once
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "default") {
      const p = await Notification.requestPermission();
      permissionRef.current = p === "granted";
    } else {
      permissionRef.current = Notification.permission === "granted";
    }
  }, []);

  // Check if any post is due within 5 min and fire notification
  const checkDue = useCallback((pending) => {
    if (!permissionRef.current) return;
    const now = Date.now();
    const fiveMin = 5 * 60 * 1000;
    (pending || posts).forEach(post => {
      const diff = new Date(post.scheduled_at) - now;
      if (diff > 0 && diff <= fiveMin && !notifiedRef.current.has(post.schedule_id)) {
        notifiedRef.current.add(post.schedule_id);
        try {
          new Notification(t("notifications.timeToPost"), {
            body: t("notifications.dueBody", { platform: post.platform, business: post.business_name, when: getCountdown(post.scheduled_at, t) }),
            icon: "/favicon.ico",
            tag: post.schedule_id,
          });
        } catch { /* silently skip if notifications not supported */ }
      }
    });
  }, [posts, t]);

  useEffect(() => {
    if (!user) return;
    requestPermission();
    fetchPosts().then(p => p && checkDue(p));
    const iv = setInterval(() => {
      fetchPosts().then(p => p && checkDue(p));
    }, 60_000);
    return () => clearInterval(iv);
  }, [user, fetchPosts, checkDue, requestPermission]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const now = Date.now();
  const upcoming24h = posts.filter(p => {
    const diff = new Date(p.scheduled_at) - now;
    return diff > 0 && diff <= 24 * 3600_000;
  });
  const overdue = posts.filter(p => new Date(p.scheduled_at) < now);
  const badgeCount = upcoming24h.length + overdue.length;

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="notification-bell-btn"
        className="relative p-2 rounded-lg hover:bg-white/5 transition-colors"
        style={{ color: badgeCount > 0 ? "#c084fc" : "#6b6b6b" }}>
        <Bell className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
        {badgeCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-white flex items-center justify-center font-bold"
            style={{ background: overdue.length > 0 ? "#f87171" : "#9333ea", fontSize: 9 }}>
            {badgeCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-full mt-2 w-80 rounded-2xl shadow-2xl z-50 overflow-hidden"
          style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}>
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "#252525" }}>
            <div className="flex items-center gap-2">
              <Bell className="w-3.5 h-3.5" style={{ color: "#c084fc" }} />
              <span className="text-sm font-bold text-white">{t("notifications.scheduledPosts")}</span>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/5 transition-colors" style={{ color: "#6b6b6b" }}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {posts.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <Calendar className="w-6 h-6 mx-auto mb-2" style={{ color: "#6b6b6b" }} />
                <p className="text-xs" style={{ color: "#6b6b6b" }}>{t("notifications.none")}</p>
              </div>
            ) : (
              [...overdue, ...upcoming24h, ...posts.filter(p => {
                const diff = new Date(p.scheduled_at) - now;
                return diff > 24 * 3600_000;
              })].map(post => {
                const isOverdue = new Date(post.scheduled_at) < now;
                const color = PLATFORM_DOT[post.platform] || "#c084fc";
                return (
                  <div key={post.schedule_id}
                    className="px-4 py-3 border-b flex items-start gap-3"
                    style={{ borderColor: "#1e1e1e" }}
                    data-testid={`notif-post-${post.schedule_id}`}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ background: color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-white truncate">{post.platform}</p>
                      <p className="text-xs truncate" style={{ color: "#6b6b6b" }}>{post.business_name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Calendar className="w-3 h-3 flex-shrink-0" style={{ color: "#6b6b6b" }} />
                        <span className="text-xs" style={{ color: "#6b6b6b" }}>
                          {new Date(post.scheduled_at).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                        <span className="text-xs font-bold" style={{ color: isOverdue ? "#f87171" : "#c084fc" }}>
                          {isOverdue ? t("notifications.overdue") : getCountdown(post.scheduled_at, t)}
                        </span>
                      </div>
                    </div>
                    {isOverdue && <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#f87171" }} />}
                  </div>
                );
              })
            )}
          </div>

          {posts.length > 0 && (
            <div className="px-4 py-2.5 border-t" style={{ borderColor: "#252525" }}>
              <Link to="/dashboard" onClick={() => setOpen(false)}
                className="text-xs font-medium transition-colors hover:text-purple-300"
                style={{ color: "#c084fc" }}>
                {t("notifications.viewAll")}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/billing/credits`, { withCredentials: true })
        .then(res => setCredits(res.data.credits))
        .catch(() => {});
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLinks = [
    { href: "/dashboard", id: "dashboard", label: t("nav.dashboard"), icon: LayoutDashboard },
    { href: "/analytics", id: "analytics", label: t("nav.analytics"), icon: BarChart2 },
    { href: "/brand-kit", id: "brand-kit", label: t("nav.brandKit"), icon: Brush },
  ];

  return (
    <div className="min-h-screen" style={{ background: "#111111" }}>
      {/* Navbar */}
      <nav className="sticky top-0 z-50 border-b" style={{ background: "rgba(17,17,17,0.95)", backdropFilter: "blur(12px)", borderColor: "#252525" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" data-testid="nav-logo">
              <Logo size="md" />
            </Link>

            {/* Nav links */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map(({ href, id, label, icon: Icon }) => (
                <Link key={href} to={href} data-testid={`nav-${id}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    location.pathname === href
                      ? "text-purple-300 bg-purple-500/10"
                      : "text-zinc-400 hover:text-zinc-200 hover:bg-white/5"
                  }`}>
                  <Icon className="w-4 h-4" />{label}
                </Link>
              ))}
            </div>

            {/* Right */}
            <div className="flex items-center gap-2">
              {/* Credits badge */}
              {credits !== null && (
                <Link to="/billing"
                  data-testid="credits-display"
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors"
                  style={{
                    background: credits < 5 ? "rgba(248,113,113,0.1)" : "rgba(168,85,247,0.08)",
                    color: credits < 5 ? "#f87171" : "#c084fc",
                    border: `1px solid ${credits < 5 ? "rgba(248,113,113,0.2)" : "rgba(168,85,247,0.15)"}`
                  }}>
                  <Zap className="w-3 h-3" />
                  {t("nav.credits", { count: credits })}
                </Link>
              )}

              <button onClick={() => navigate("/projects/new")} data-testid="nav-create-ad-btn"
                className="ghost-purple text-sm px-4 py-2 rounded-full font-semibold flex items-center gap-1.5 whitespace-nowrap">
                <Plus className="w-4 h-4" /> {t("nav.newAd")}
              </button>

              <LocaleSwitcher />

              <NotificationBell user={user} />

              <div className="relative">
                <button onClick={() => setShowUserMenu(!showUserMenu)} data-testid="nav-user-menu-btn"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "rgba(168,85,247,0.2)" }}>
                      <span className="text-purple-300 text-xs font-bold">{user?.name?.[0]?.toUpperCase() || "U"}</span>
                    </div>
                  )}
                  <span className="text-sm font-medium text-zinc-400 hidden sm:block max-w-[120px] truncate">{user?.name}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-1 w-48 rounded-xl shadow-2xl py-1 z-50" style={{ background: "#1c1c1c", border: "1px solid #2d2d2d" }}>
                    <div className="px-3 py-2 border-b" style={{ borderColor: "#252525" }}>
                      <p className="text-xs" style={{ color: "#6b6b6b" }}>{t("nav.signedInAs")}</p>
                      <p className="text-sm font-medium text-zinc-300 truncate">{user?.email}</p>
                    </div>
                    <Link to="/billing" onClick={() => setShowUserMenu(false)}
                      data-testid="nav-billing-link"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:bg-white/5 transition-colors">
                      <CreditCard className="w-4 h-4" /> {t("nav.creditsBilling")}
                    </Link>
                    <button onClick={handleLogout} data-testid="nav-logout-btn"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors">
                      <LogOut className="w-4 h-4" /> {t("nav.signOut")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Page content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      <Footer />
    </div>
  );
}
